import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useDismissOrderChangeBanner} from '../../../../common/react-query/hooks/mutations/useDismissOrderChangeBanner'
import {useGetDismissedOrderChangeBanners} from '../../../../common/react-query/hooks/queries/useGetDismissedOrderChangeBanners'
import {useGetLatestOrderChangeRequest} from '../../../../common/react-query/hooks/queries/useGetLatestOrderChangeRequest'
import {hubQueryClient} from '../../../../common/react-query/QueryProvider'
import {
  UseOrderChangeBannerOptions,
  UseOrderChangeBannerResult
} from '../types/orderChangeBanner.types'
import {BannerState, determineBannerState} from '../utils/determineBannerState'

import {useAutoRefreshOnManualApproval} from './useAutoRefreshOnManualApproval'
import {usePropagationDelay} from './usePropagationDelay'

/** Polling interval for auto-approval pending state (10 seconds) */
const POLLING_INTERVAL_MS = 10000

/** Delay to allow backend item-level data to propagate after changesApplied (5 seconds) */
const PROPAGATION_DELAY_MS = 5000

/** Minimum duration to show the refresh overlay so users see the loading state (3 seconds) */
const REFRESH_OVERLAY_MIN_DURATION_MS = 3000

/**
 * Time-to-live for the changesApplied banner, measured from the request's
 * modifiedOn (or createdOn) timestamp. Using a server timestamp makes the
 * banner lifetime deterministic and navigation-proof — navigating away and
 * back recalculates the remaining time instead of restarting a client timer.
 */
const CHANGES_APPLIED_TTL_MS = 120000

/**
 * Computes effective banner state, bridging with processing overlay during
 * auto-refresh or propagation delay.
 */
const getEffectiveBannerState = (
  finalBannerState: BannerState,
  isAutoRefreshing: boolean,
  isPropagating: boolean
) => {
  const showProcessing = isAutoRefreshing || isPropagating
  return {
    showBanner: showProcessing || finalBannerState.showBanner,
    variant: showProcessing ? ('changeRequestProcessing' as const) : finalBannerState.variant,
    hasProcessingRequest: finalBannerState.variant === 'changeRequestProcessing' || showProcessing,
    hasActiveRequest: finalBannerState.showBanner || showProcessing
  }
}

/**
 * Hook to manage the order change banner display logic.
 *
 * This hook:
 * 1. Fetches the latest order change request on mount
 * 2. Fetches dismissed banners from distributed user storage
 * 3. Determines if/which banner variant to show
 * 4. Polls for updates when waiting for changes to be applied
 * 5. For auto-approval: shows "changesApplied" banner with a refresh button
 * 6. For manual approval: auto-refreshes order data when changesApplied transitions to true,
 *    bridging with processing overlay until Redux has fresh orderStatus.
 *    Banner auto-hides when dispatcher confirms (orderStatus leaves unconfirmed state).
 *
 * @param options - Configuration options including orderId and orderStatus
 * @returns Banner state and actions
 */
export const useOrderChangeBanner = ({
  orderId,
  orderStatus,
  enabled = true,
  onOrderDataChanged
}: UseOrderChangeBannerOptions): UseOrderChangeBannerResult => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isBannerExpired, setIsBannerExpired] = useState(false)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset local state when switching to a different order
  useEffect(() => {
    setIsRefreshing(false)
    setIsBannerExpired(false)
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }, [orderId])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [])

  const {data: dismissedBanners = []} = useGetDismissedOrderChangeBanners(enabled)

  const {data: initialData, isLoading: isInitialLoading} = useGetLatestOrderChangeRequest(
    orderId,
    enabled,
    false
  )

  const bannerState = useMemo(
    () => determineBannerState(initialData, dismissedBanners, orderStatus),
    [initialData, dismissedBanners, orderStatus]
  )
  const shouldPoll = bannerState.variant === 'changeRequestProcessing'

  const {
    data: request,
    isLoading: isPollingLoading,
    isFetching
  } = useGetLatestOrderChangeRequest(
    orderId,
    enabled && shouldPoll,
    shouldPoll ? POLLING_INTERVAL_MS : false
  )

  const latestRequest = shouldPoll ? request : initialData
  const isLoading = shouldPoll ? isPollingLoading : isInitialLoading

  const finalBannerState = useMemo(
    () => determineBannerState(latestRequest, dismissedBanners, orderStatus),
    [latestRequest, dismissedBanners, orderStatus]
  )

  const isAutoRefreshing = useAutoRefreshOnManualApproval(
    latestRequest,
    onOrderDataChanged,
    orderId
  )

  // Propagation delay: keep processing overlay for 5s after changesApplied
  // to allow backend item-level data to propagate before showing refresh button
  const isPropagating = usePropagationDelay(latestRequest, PROPAGATION_DELAY_MS, orderId)

  const {mutate: dismissBanner} = useDismissOrderChangeBanner()

  // Timestamp-based expiration for changesApplied banner.
  // Calculates remaining TTL from the server timestamp on every mount/navigation,
  // so the banner auto-dismisses even if the user navigated away and back.
  useEffect(() => {
    if (finalBannerState.variant !== 'changesApplied') {
      setIsBannerExpired(false)
      return
    }

    const timestamp = latestRequest?.modifiedOn ?? latestRequest?.createdOn
    if (!timestamp) {
      setIsBannerExpired(false)
      return
    }

    const age = Date.now() - new Date(timestamp).getTime()

    if (age >= CHANGES_APPLIED_TTL_MS) {
      setIsBannerExpired(true)
      if (latestRequest?.requestId) {
        dismissBanner({requestId: latestRequest.requestId})
      }
      return
    }

    setIsBannerExpired(false)
    const remaining = CHANGES_APPLIED_TTL_MS - age
    const timer = setTimeout(() => {
      setIsBannerExpired(true)
      if (latestRequest?.requestId) {
        dismissBanner({requestId: latestRequest.requestId})
      }
    }, remaining)

    return () => clearTimeout(timer)
  }, [
    latestRequest?.modifiedOn,
    latestRequest?.createdOn,
    latestRequest?.requestId,
    finalBannerState.variant,
    dismissBanner
  ])

  const onRefresh = useCallback(() => {
    if (isRefreshing) return
    setIsRefreshing(true)

    hubQueryClient.invalidateQueries(['order', orderId])
    hubQueryClient.invalidateQueries('orders')
    onOrderDataChanged?.()

    // Keep the overlay visible for a minimum duration while Redux fetches fresh data.
    // fetchOrders is a Redux thunk that doesn't return a promise, so we use a fixed delay.
    refreshTimerRef.current = setTimeout(() => {
      setIsRefreshing(false)
      refreshTimerRef.current = null
    }, REFRESH_OVERLAY_MIN_DURATION_MS)
  }, [orderId, onOrderDataChanged, isRefreshing])

  const effective = getEffectiveBannerState(finalBannerState, isAutoRefreshing, isPropagating)

  // Hide the banner if it has expired based on request timestamp
  const showBanner = effective.showBanner && !isBannerExpired
  const variant = isBannerExpired ? null : effective.variant

  return {
    showBanner,
    variant,
    hasProcessingRequest: effective.hasProcessingRequest || isRefreshing,
    hasActiveRequest: effective.hasActiveRequest,
    onRefresh,
    isLoading: isLoading || isFetching,
    isRefreshing
  }
}
